import {MapRenderer} from '@/map/MapRenderer';
import Polygon = Phaser.GameObjects.Polygon;
import Scene = Phaser.Scene;
import Vector2 = Phaser.Math.Vector2;

export class HighlightManager {
  private hexWidth: number;
  private hexHeight: number;
  private hexSide: number;
  private hexTop: number;

  private cache: Polygon[] = [];
  private active: Map<Vector2, Polygon> = new Map();

  constructor(hexWidth: number, hexHeight: number, hexSide: number) {
    this.hexWidth = hexWidth;
    this.hexHeight = hexHeight;
    this.hexSide = hexSide;
    this.hexTop = (hexHeight - hexSide) / 2;
  }

  setHighlight(renderer: MapRenderer, x: number, y: number, isHighlight: boolean) {
    const v2 = new Vector2(x, y);
    if (isHighlight == this.active.has(v2)) {
      return;
    }

    if (isHighlight) {
      const worldPos = renderer.hexToWorld(x, y);
      if (!worldPos) {
        console.warn(`Failed to get world position for hex at ${x},${y}`);
        return;
      }
      const polygon = this.getOrCreate(renderer.scene);
      polygon.setPosition(worldPos.x, worldPos.y);
      this.active.set(v2, polygon);
    } else {
      const polygon = this.active.get(v2);
      if (polygon) {
        this.release(polygon);
      }
      this.active.delete(v2);
    }
  }

  clear() {
    for (const polygon of this.active.values()) {
      this.release(polygon);
    }
    this.active.clear();
  }

  private getOrCreate(scene: Scene): Polygon {
    let polygon: Polygon | undefined = undefined;
    if (this.cache.length > 0) {
      polygon = this.cache.pop();
    } else {
      const halfWidth = this.hexWidth / 2;
      // Create a new one. Note - polygons are built with 0,0 in the top left, but are
      // positioned based on an anchor point that is the center of the polygon.
      const points: Vector2[] = [
        new Vector2(halfWidth, 0),
        new Vector2(this.hexWidth, this.hexTop),
        new Vector2(this.hexWidth, this.hexTop + this.hexSide),
        new Vector2(halfWidth, this.hexHeight),
        new Vector2(0, this.hexTop + this.hexSide),
        new Vector2(0, this.hexTop)
      ];
      const created = scene.add.polygon(0, 0, points, 0xffff00, 0.25);
      if (!created) {
        throw new Error('Failed to create polygon for highlight.');
      }
      created.setStrokeStyle(4, 0xffff00, 1);
      polygon = created;
    }

    if (!polygon) throw new Error('Failed to create polygon for highlight.');

    polygon.setVisible(true);
    polygon.setActive(true);
    return polygon;
  }

  private release(polygon: Polygon) {
    polygon.setVisible(false);
    polygon.setActive(false);
    this.cache.push(polygon);
  }
}
